
// Toast Types: success, error, warning, info

import { createContext, useCallback, useContext, useState, useEffect } from "react"
import './toast.css';

const ToastContext = createContext({
    show: ({ type, message, messageSecondary, duration = 3500, width = "auto", height = 20 }) => { }
});

const initialState = {
    isVisible: false,
    type: "info",
    message: "",
    messageSecondary: "",
    duration: 3500,
    width: "auto",
    height: 20
};

function ToastProvider({ children }) {
    const [toastConfig, setToastConfig] = useState(initialState)
    const [isHiding, setIsHiding] = useState(false)

    const show = useCallback(({ type, message, messageSecondary, duration = 3500, width = "auto", height = 20 }) => {
        setToastConfig({
            isVisible: true,
            duration,
            type,
            message,
            messageSecondary
        })
        setIsHiding(false)
    }, [])

    const hide = useCallback(() => {
        setIsHiding(true)
        setTimeout(() => {
            setToastConfig(initialState)
            setIsHiding(false)
        }, 500) // wait for fade-out animation
    }, [])

    useEffect(() => {
        if (toastConfig.isVisible) {
            const timer = setTimeout(() => {
                hide()
            }, toastConfig.duration)

            return () => clearTimeout(timer)
        }
    }, [toastConfig.isVisible, toastConfig.duration, hide])

    const getToastColor = () => {
        switch (toastConfig.type) {
            case 'success':
                return '#28a745' // green
            case 'error':
                return '#dc3545' // red
            case 'warning':
                return '#ffc107' // yellow/orange
            case 'info':
            default:
                return '#17a2b8' // blue
        }
    }

    const getToastIcon = () => {
        switch (toastConfig.type) {
            case 'success':
                return 'fa-solid fa-circle-check'
            case 'error':
                return 'fa-solid fa-circle-xmark'
            case 'warning':
                return 'fa-solid fa-triangle-exclamation'
            case 'info':
            default:
                return 'fa-solid fa-circle-info'
        }
    }

    if (!toastConfig.isVisible) return <ToastContext.Provider value={show}>{children}</ToastContext.Provider>

    return (
        <ToastContext.Provider value={show}>
            {children}
            <div 
                className={`custom-toast ${isHiding ? 'hide' : ''}`}
                style={{ backgroundColor: getToastColor() }}
            >
                <div className="toast-icon">
                    <i className={getToastIcon()}></i>
                </div>

                <div className="toast-content">
                    <div className="line1">{toastConfig.message}</div>
                    {toastConfig.messageSecondary && (
                        <div className="line2">{toastConfig.messageSecondary}</div>
                    )}
                </div>


                <button className="toast-close" onClick={hide}>×</button>
            </div>
        </ToastContext.Provider>
    )
}

const useToast = () => useContext(ToastContext);
export { ToastProvider, useToast }